import { useCallback, useMemo } from 'react';
import { SubjectKeyboardType } from '@surveycake/logic-engine';
import {
  useEngineInstance,
  useSubjectId
} from '@surveycake/react-logic-engine';
import { useOnKeyDownEnter } from '@surveycake/rhooks';
import { useSurveyConfig } from 'app/core/survey-config';
import { useHandleAnswerFinish } from 'app/modules/subject';
import { createUseInputRefAndSetAnswer } from 'app/modules/subject';

const useTextShortInputRefAndSetAnswer = createUseInputRefAndSetAnswer<
  string,
  HTMLInputElement
>(
  (inputEl, value) => {
    inputEl.value = value || '';
  },
  inputEl => {
    let { value } = inputEl;
    const type = (inputEl.getAttribute('type') ||
      'text') as SubjectKeyboardType;

    /**
     * Auto fill prefix of protocol if there is no any.
     */
    if (type === 'url' && value && !/^(https|http|ftp):\/\/\w*/.test(value)) {
      value = `https://${value}`;
    }

    return value ? [value] : [];
  }
);

export function useTextShortAnswer() {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const keyboardType = useMemo(() => engine.getSubjectData(id).keyboardType, [
    engine,
    id
  ]);
  const { wording } = useSurveyConfig();
  const placeholder = wording.message.placeholder.input[keyboardType] || '';
  const { inputRef, onChange, setAnswer } = useTextShortInputRefAndSetAnswer(
    engine,
    id
  );
  const handleAnswerFinish = useHandleAnswerFinish(engine, id);
  const onKeyDown = useOnKeyDownEnter<HTMLInputElement>(
    useCallback(async event => {
      event.preventDefault();
      await setAnswer();
      handleAnswerFinish();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  return {
    ref: inputRef,
    placeholder,
    type: keyboardType,
    onChange,
    onBlur: setAnswer,
    onKeyDown,
    'data-subject-answer': id
  };
}
